// React Dependencies
import React, { useEffect, useMemo, useState } from 'react';

// External Dependencies
import { Button, CircularProgress, Divider, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CSVLink } from 'react-csv';

// Internal Dependencies
import MText from '../../../../components/Common/MText';
import TotalCircleComp from '../../../../components/Analytics/TotalCircleComp';
import Colours from '../../../../styles/colours';
import ResponsiveChart from './ResponsiveChart';
import Constants from '../../../../common/constants';
import ApiService from '../../../../services/apiService';
import { IsEmpty } from '../../../../common/checks';

const buttonColor = Colours.LightBlue;

const useStyles = makeStyles({
  exportBtnRoot: {
    backgroundColor: buttonColor,
    padding:         '8px 20px',
    borderRadius:    4,
    '&:hover':       {
      backgroundColor: buttonColor,
    },
  },
  exportBtnLabel: {
    fontWeight: 'normal',
    color:      Colours.White,
    fontSize:   '.7rem',
  },
  dividerRoot: {
    height:       3,
    marginTop:    40,
    marginBottom: 40,
  },
});

const momentCurrentDate = moment().utcOffset('+08:00');

const generatePeriod = (periodType) => {
  const period = [];
  if (periodType === Constants.PeriodType.PastWeek) {
    for (let i = 7; i >= 1; i -= 1) {
      period.push(momentCurrentDate.clone().subtract(i, 'days').format('DD MMM'));
    }
  } else if (periodType === Constants.PeriodType.PastMonth) {
    const previousMonth = momentCurrentDate.clone().subtract(1, 'month');
    const daysInMonth = previousMonth.daysInMonth();
    const customWeeks = Math.ceil(daysInMonth / 7);

    for (let i = 0; i < customWeeks; i += 1) {
      period.push(`Week ${i + 1}`);
    }
  } else if (periodType === Constants.PeriodType.YTD) {
    const currentMonth = moment().month();
    for (let i = 0; i <= currentMonth; i += 1) {
      period.push(momentCurrentDate.clone().month(i).format('MMM YYYY'));
    }
  }

  return period;
};

const PeriodStatsPanel = (props) => {
  const classes = useStyles();
  const [statistic, setStatistic] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [outletReceiptAggregate, setOuletReceiptAggregate] = useState({});
  const [outletStampAggregate, setOutletStampAggregate] = useState({});
  const [totalDaysInPeriod, setTotalDaysInPeriod] = useState(7);
  const [loyaltyCardName, setLoyaltyCardName] = useState('');
  const [periodRange, setPeriodRange] = useState('');

  const fetchLoyaltyCardStatisticByPeriod = async (pageId, loyaltyCardId, period) => {
    try {
      setIsLoading(true);
      const response = await ApiService.getLoyaltyCardStatisticByPeriod(pageId, loyaltyCardId, period);
      setStatistic(response);

      let outletSums = {};
      if (!IsEmpty(response?.charts?.receiptUpload)) {
        response.charts.receiptUpload.forEach((p) => {
          p.statistics.forEach((stat) => {
            if (!outletSums[stat.outletName]) {
              outletSums[stat.outletName] = 0;
            }
            outletSums[stat.outletName] += stat.count;
          });
        });
      }
      setOuletReceiptAggregate(outletSums);

      outletSums = {};

      if (!IsEmpty(response?.charts?.stamp)) {
        response.charts.stamp.forEach((p) => {
          p.statistics.forEach((stat) => {
            if (!outletSums[stat.outletName]) {
              outletSums[stat.outletName] = 0;
            }
            outletSums[stat.outletName] += stat.count;
          });
        });
      }
      setOutletStampAggregate(outletSums);
    } catch (error) {
      toast.error('Error in fetchLoyaltyCardStatisticByPeriod:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const summaries = [
    {
      name:  'New sign-ups',
      value: statistic?.newSignUps ?? '-',
    },
    {
      name:  'Uploads Total',
      value: statistic?.receiptUploads ?? '-',
    },
    {
      name:  'Uploads PDA',
      value: statistic?.receiptUploadsPDA.toFixed(2) ?? '-',
    },
    {
      name:  'Stamps',
      value: statistic?.stamps ?? '-',
    },
    {
      name:  'Vouchers issued',
      value: statistic?.vouchersIssued ?? '-',
    },
    {
      name:  'Vouchers claimed',
      value: statistic?.vouchersClaimed ?? '-',
    },
  ];

  const receiptUploadsChartData = useMemo(() => {
    const receiptUploads = statistic?.charts?.receiptUpload;

    if (!IsEmpty(receiptUploads)) {
      return receiptUploads.map((d) => ({
        name: d.period,
        ...(d.statistics.reduce((acc, item) => {
          acc[item.outletName] = item.count;
          acc.total = (acc.total || 0) + item.count;
          return acc;
        }, {})),
      }));
    }

    return [...generatePeriod(props.period).map((period) => ({
      total: 0,
      name:  period,
    }))];
  }, [statistic, props.period]);

  const stampsChartData = useMemo(() => {
    const stamps = statistic?.charts?.stamp;

    if (!IsEmpty(stamps)) {
      return stamps.map((d) => ({
        name: d.period,
        ...(d.statistics.reduce((acc, item) => {
          acc[item.outletName] = item.count;
          acc.total = (acc.total || 0) + item.count;
          return acc;
        }, {})),
      }));
    }

    return [...generatePeriod(props.period).map((period) => ({
      total: 0,
      name:  period,
    }))];
  }, [statistic, props.period]);

  const colors = ['#ff679a', '#15ae5c', '#0e99ff'];
  const outletsName = statistic?.charts?.receiptUpload[0]?.statistics.map((stat) => stat.outletName) || [];

  const outletsWithColors = outletsName.map((item, index) => ({
    key:    item,
    colour: colors[index % colors.length],
  }));

  const getLoyaltyCard = async () => {
    try {
      const res = await ApiService.getLoyaltyCard(props.pageId, props.loyaltyCardId);

      const { name } = res;
      setLoyaltyCardName(name);
    } catch (err) {
      console.log(err);
      toast.error(err.message);
    }
  };

  useEffect(() => {
    getLoyaltyCard();
  }, [props.loyaltyCardId, props.pageId]);

  useEffect(() => {
    if (props.period === Constants.PeriodType.PastWeek) {
      setTotalDaysInPeriod(7);
    } else if (props.period === Constants.PeriodType.PastMonth) {
      const totalDaysDivider = momentCurrentDate.clone().subtract(1, 'month').daysInMonth();
      setTotalDaysInPeriod(totalDaysDivider);
    } else {
      // Get yesterday's date (e.g., April 8)
      const yesterday = momentCurrentDate.clone().subtract(1, 'days');

      // Get the first day of the year (e.g., January 1)
      const startOfYear = momentCurrentDate.clone().startOf('year');

      // Calculate the inclusive difference in days
      const totalDaysDivider = yesterday.diff(startOfYear, 'days') + 1;
      setTotalDaysInPeriod(totalDaysDivider);
    }
  }, [props.period]);

  useEffect(() => {
    if (props.period === Constants.PeriodType.PastWeek) {
      const start = moment()
        .utcOffset('+08:00')
        .subtract(1, 'week')
        .startOf('day')
        .format('D');

      const end = moment()
        .utcOffset('+08:00')
        .subtract(1, 'day')
        .startOf('day')
        .format('D MMM');

      setPeriodRange(`${start} - ${end}`);
    } else if (props.period === Constants.PeriodType.PastMonth) {
      const period = moment()
        .utcOffset('+08:00')
        .subtract(1, 'month')
        .startOf('month')
        .format('MMM YYYY');

      setPeriodRange(period);
    } else if (props.period === Constants.PeriodType.YTD) {
      const [, month, day] = momentCurrentDate.clone().format('YYYY-MM-DD').split('-');
      let start;
      let end;

      if (month === '01' && day === '01') {
        start = moment()
          .utcOffset('+08:00')
          .subtract(1, 'year')
          .startOf('year')
          .format('D MMM');

        end = moment()
          .utcOffset('+08:00')
          .subtract(1, 'year')
          .endOf('year')
          .format('D MMM YYYY');
      } else {
        start = moment()
          .utcOffset('+08:00')
          .startOf('year')
          .format('D MMM');

        end = moment()
          .utcOffset('+08:00')
          .subtract(1, 'day')
          .startOf('day')
          .format('D MMM');
      }

      setPeriodRange(`${start} - ${end}`);
    } else {
      setPeriodRange('');
    }
  }, [props.period]);

  useEffect(() => {
    fetchLoyaltyCardStatisticByPeriod(props.pageId, props.loyaltyCardId, props.period);
  }, [props.pageId, props.loyaltyCardId, props.period]);

  if (isLoading) {
    return (
      <div className="d-flex flex-row justify-content-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <MText
        bold
        size=".9rem"
        text={`PERIOD: ${props.period} (${periodRange})`}
        style={{
          textDecoration: 'underline',
        }}
      />
      <div className="row">
        {
          summaries.map((summary) => (
            <div key={summary.name} className="col-6 col-sm-4 col-xl-2 d-flex flex-column align-items-center">
              <TotalCircleComp
                totalCount={summary.value}
                dimension="120px"
                showTitle={false}
                borderColor={Colours.Gray4}
              />
              <MText colour={Colours.Gray1} text={summary.name} />
            </div>
          ))
        }
      </div>

      {/* Receipt Uploads */}
      <div className="mt-5 ml-5">
        <MText
          bold
          size=".9rem"
          text="RECEIPT UPLOADS"
        />
        <div
          className="row"
          style={{
            rowGap: 20,
          }}
        >
          <div className="col-12 col-xl-7 pl-0 d-flex flex-column">
            <ResponsiveChart
              data={receiptUploadsChartData}
              outletsKey={outletsWithColors}
              xAxisDataKey="name"
            />
          </div>
          <div className="col-12 pl-0 col-xl-5 d-flex flex-column">
            <div>
              <div className="d-flex flex-row justify-content-end">
                <CSVLink
                  headers={[
                    { label: 'Outlet', key: 'outlet' },
                    { label: 'Uploads Total', key: 'uploads' },
                    { label: 'Uploads PDA', key: 'uploadsPDA' },
                  ]}
                  data={[...Object.keys(outletReceiptAggregate).map((key) => ({
                    outlet:     key,
                    uploads:    outletReceiptAggregate[key],
                    uploadsPDA: (outletReceiptAggregate[key] / totalDaysInPeriod).toFixed(2),
                  })), {
                    outlet:     'Total',
                    uploads:    Object.keys(outletReceiptAggregate).reduce((acc, curr) => acc + outletReceiptAggregate[curr], 0),
                    uploadsPDA: (Object.keys(outletReceiptAggregate).reduce((acc, curr) => acc + outletReceiptAggregate[curr], 0) / totalDaysInPeriod).toFixed(2),
                  }]}
                  filename={`${loyaltyCardName} - Receipt Uploads - ${props.period}.csv`}
                >
                  <Button
                    classes={{
                      root:  classes.exportBtnRoot,
                      label: classes.exportBtnLabel,
                    }}
                  >
                    Export Data
                  </Button>
                </CSVLink>
              </div>
              <div>
                <MText
                  size=".9rem"
                  text="Uploads by outlet"
                />
                <table
                  className="table table-bordered bg-white mt-2"
                  style={{
                    borderRadius:   12,
                    overflow:       'hidden',
                    borderCollapse: 'separate',
                    borderSpacing:  0,
                  }}
                >
                  <thead>
                    <tr>
                      <th scope="col" style={{ verticalAlign: 'middle' }}>Outlet</th>
                      <th scope="col" className="text-center" style={{ verticalAlign: 'middle', width: 40 }}>Uploads Total</th>
                      <th scope="col" className="text-center" style={{ verticalAlign: 'middle', width: 40 }}>Uploads PDA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      Object.keys(outletReceiptAggregate).map((key) => (
                        <tr key={key}>
                          <td>{key}</td>
                          <td className="text-center">{outletReceiptAggregate[key]}</td>
                          <td className="text-center">{(outletReceiptAggregate[key] / totalDaysInPeriod).toFixed(2)}</td>
                        </tr>
                      ))
                    }
                    <tr>
                      <td>TOTAL</td>
                      <td className="text-center">
                        {Object.keys(outletReceiptAggregate).reduce((acc, curr) => acc + outletReceiptAggregate[curr], 0)}
                      </td>
                      <td className="text-center">
                        {(Object.keys(outletReceiptAggregate).reduce((acc, curr) => acc + outletReceiptAggregate[curr], 0) / totalDaysInPeriod).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div>
                <MText
                  size=".9rem"
                  text="Per Store Average"
                />
                <div className="row">
                  <div className="col-6 d-flex flex-column align-items-center">
                    <TotalCircleComp
                      totalCount={(Object.keys(outletReceiptAggregate).reduce((acc, curr) => acc + outletReceiptAggregate[curr], 0) / outletsName.length).toFixed(2)}
                      dimension="120px"
                      showTitle={false}
                      borderColor={Colours.Gray4}
                    />
                    <MText text="Uploads Total" />
                  </div>
                  <div className="col-6 d-flex flex-column align-items-center">
                    <TotalCircleComp
                      totalCount={((Object.keys(outletReceiptAggregate).reduce((acc, curr) => acc + outletReceiptAggregate[curr], 0) / totalDaysInPeriod) / outletsName.length).toFixed(2)}
                      dimension="120px"
                      showTitle={false}
                      borderColor={Colours.Gray4}
                    />
                    <MText text="Uploads PDA" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Divider classes={{ root: classes.dividerRoot }} />

      {/* Stamps Issued */}
      <div className="mt-5 ml-5">
        <MText
          bold
          size=".9rem"
          text="STAMPS ISSUED"
        />
        <div
          className="row"
          style={{ rowGap: 20 }}
        >
          <div className="col-12 col-xl-7 pl-0 d-flex flex-column">
            <ResponsiveChart
              data={stampsChartData}
              outletsKey={outletsWithColors}
              xAxisDataKey="name"
            />
          </div>
          <div className="col-12 col-xl-5 pl-0 d-flex flex-column">
            <div>
              <div className="d-flex flex-row justify-content-end">
                <CSVLink
                  headers={[
                    { label: 'Outlet', key: 'outlet' },
                    { label: 'Total Stamps per store average', key: 'stamps' },
                    { label: 'Daily stamps per store average', key: 'stampsPDA' },
                  ]}
                  data={[...Object.keys(outletStampAggregate).map((key) => ({
                    outlet:    key,
                    stamps:    outletStampAggregate[key],
                    stampsPDA: (outletStampAggregate[key] / totalDaysInPeriod).toFixed(2),
                  })), {
                    outlet:    'Total',
                    stamps:    Object.keys(outletStampAggregate).reduce((acc, curr) => acc + outletStampAggregate[curr], 0),
                    stampsPDA: (Object.keys(outletStampAggregate).reduce((acc, curr) => acc + outletStampAggregate[curr], 0) / totalDaysInPeriod).toFixed(2),
                  }]}
                  filename={`${loyaltyCardName} - Stamps - ${props.period}.csv`}
                >
                  <Button
                    classes={{
                      root:  classes.exportBtnRoot,
                      label: classes.exportBtnLabel,
                    }}
                  >
                    Export Data
                  </Button>
                </CSVLink>
              </div>
              <div>
                <MText
                  size=".9rem"
                  text="Stamps by outlet"
                />
                <table
                  className="table table-bordered bg-white mt-2"
                  style={{
                    borderRadius:   12,
                    overflow:       'hidden',
                    borderCollapse: 'separate',
                    borderSpacing:  0,
                  }}
                >
                  <thead>
                    <tr>
                      <th scope="col" style={{ verticalAlign: 'middle' }}>Outlet</th>
                      <th scope="col" className="text-center" style={{ verticalAlign: 'middle', width: 40 }}>Stamps</th>
                      <th scope="col" className="text-center" style={{ verticalAlign: 'middle', width: 40 }}>Stamps PDA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      Object.keys(outletStampAggregate).map((key) => (
                        <tr key={key}>
                          <td>{key}</td>
                          <td className="text-center">{outletStampAggregate[key]}</td>
                          <td className="text-center">{(outletStampAggregate[key] / totalDaysInPeriod).toFixed(2)}</td>
                        </tr>
                      ))
                    }
                    <tr>
                      <td>TOTAL</td>
                      <td className="text-center">
                        {Object.keys(outletStampAggregate).reduce((acc, curr) => acc + outletStampAggregate[curr], 0)}
                      </td>
                      <td className="text-center">
                        {(Object.keys(outletStampAggregate).reduce((acc, curr) => acc + outletStampAggregate[curr], 0) / totalDaysInPeriod).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div>
                <MText
                  size=".9rem"
                  text="Per Store Average"
                />
                <div className="row">
                  <div className="col-6 d-flex flex-column align-items-center">
                    <TotalCircleComp
                      totalCount={(Object.keys(outletStampAggregate).reduce((acc, curr) => acc + outletStampAggregate[curr], 0) / outletsName.length).toFixed(2)}
                      dimension="120px"
                      showTitle={false}
                      borderColor={Colours.Gray4}
                    />
                    <MText text="Total stamps per store average" />
                  </div>
                  <div className="col-6 d-flex flex-column align-items-center">
                    <TotalCircleComp
                      totalCount={((Object.keys(outletStampAggregate).reduce((acc, curr) => acc + outletStampAggregate[curr], 0) / totalDaysInPeriod) / outletsName.length).toFixed(2)}
                      dimension="120px"
                      showTitle={false}
                      borderColor={Colours.Gray4}
                    />
                    <MText text="Daily stamps per store average" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PeriodStatsPanel.propTypes = {
  period:        PropTypes.oneOf(Constants.PeriodType.PastWeek, Constants.PeriodType.PastMonth, Constants.PeriodType.YTD).isRequired,
  loyaltyCardId: PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
  pageId:        PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
};

export default PeriodStatsPanel;
